import { inject, Injectable } from '@angular/core';
import { MOMENT_DATE_FORMAT_DASH_REVERSED } from '@app/app.constants';
import { HttpUtils } from '@core/utils/http-utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { BundleListDialogComponent } from '../components/discount/bundle-list-dialog/bundle-list-dialog.component';
import { DiscountFormComponent } from '../components/discount/discount-form/discount-form.component';
import { DiscountFilters } from '../store/discount.state';
import {
  CreateDiscountPayload,
  Discount,
  DiscountApiModelData,
  DiscountPriceType,
  DiscountType,
  ValueType,
} from '../types/pricing.types';

@Injectable({
  providedIn: 'root',
})
export class DiscountUtilsService {
  private ngbModal = inject(NgbModal);

  public openDiscountForm(id?: string) {
    const modalRef = this.ngbModal.open(DiscountFormComponent, {
      centered: true,
      modalDialogClass: 'modal-size-lg',
    });

    if (id) {
      modalRef.componentInstance.discountId.set(id);
    }
  }

  public openBundleListDialog(bundleIds: string[]) {
    const modalRef = this.ngbModal.open(BundleListDialogComponent, {
      size: 'md',
      centered: true,
    });

    modalRef.componentInstance.bundleIds.set(bundleIds);
  }

  public buildDiscountHttpParams(params: DiscountFilters) {
    let httpParams = HttpUtils.buildHttpParams();

    if (params.partnerId) {
      httpParams = httpParams.set('partner_id', params.partnerId);
    }

    if (params.countries?.length) {
      httpParams = httpParams.set('countries', params.countries.join(','));
    }

    if (params.limit) {
      httpParams = httpParams.set('limit', params.limit);
    }

    if (params.page) {
      httpParams = httpParams.set('page', params.page);
    }

    if (params.discountType) {
      httpParams = httpParams.set('discount_type', params.discountType);
    }

    if (params.priceType) {
      httpParams = httpParams.set('price_type', params.priceType);
    }

    if (params.validFrom) {
      httpParams = httpParams.set(
        'valid_from',
        moment(params.validFrom).format(MOMENT_DATE_FORMAT_DASH_REVERSED)
      );
    }

    if (params.validTo) {
      httpParams = httpParams.set(
        'valid_to',
        moment(params.validTo).format(MOMENT_DATE_FORMAT_DASH_REVERSED)
      );
    }

    if (params.notes) {
      httpParams = httpParams.set('notes', params.notes);
    }

    if (params.status) {
      httpParams = httpParams.set('status', params.status);
    }

    return httpParams;
  }

  public discountToApiModel(
    discount: Discount | CreateDiscountPayload
  ): DiscountApiModelData {
    const model: DiscountApiModelData = {
      partner_id: discount?.partnerId,
      partner_ids: 'partnerIds' in discount ? discount.partnerIds : undefined,
      discount_type: discount.discountType,
      price_type: discount.priceType ?? DiscountPriceType.WSP,
      bundle_ids: discount?.bundleIds,
      reference_key: discount?.serviceDataReferenceKey,
      discount_value: discount.discountValue.toString(),
      discount_value_type: discount.valueType,
      notes: discount.reason,
      from_date: moment(discount.validFrom).format(
        MOMENT_DATE_FORMAT_DASH_REVERSED
      ),
      to_date: moment(discount.validTo).format(
        MOMENT_DATE_FORMAT_DASH_REVERSED
      ),
      active: discount.active,
      last_status: discount.lastStatus,
    };

    if (discount?.id) {
      model.id = discount.id;
    }

    return model;
  }

  public apiModelToDiscount(model: DiscountApiModelData): Discount {
    return {
      id: model?.id,
      partnerId: model?.partner_id,
      discountType: model.discount_type as DiscountType,
      priceType:
        (model.price_type as DiscountPriceType) ?? DiscountPriceType.WSP,
      bundleIds: model?.bundle_ids,
      serviceDataReferenceKey: model?.reference_key,
      discountValue: parseFloat(model.discount_value),
      valueType: model.discount_value_type as ValueType,
      validFrom: new Date(model.from_date),
      validTo: new Date(model.to_date),
      reason: model.notes,
      active: model.active,
      lastStatus: model.last_status,
      partner: {
        id: model.partner?.id,
        name: model.partner?.name,
        country: model.partner?.country,
        email: model.partner?.email,
        phone: model.partner?.phone,
        currency: model.partner?.currency,
        notes: model.partner?.notes,
        active: model.partner?.active,
        missingPrices: model.partner?.missing_prices,
      },
      createdAt: new Date(model.created_at),
      createdBy: model.created_by,
      ...(model.pivot
        ? {
            pivot: {
              serviceDataId: model.pivot.service_data_id,
              discountId: model.pivot.discount_id,
              amount:
                model.pivot.amount != null ? Number(model.pivot.amount) : null,
            },
          }
        : {}),
    };
  }
}
